import { createTheme, responsiveFontSizes } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { EdumiamTheme, WLtheme, WStheme } from './theme'

// Direction artistique des sites vitrines walter-learning.com et walter-learning.com/sante, relevée
// depuis les variables CSS `:root` qu'ils partagent. Cf. docs/direction-artistique.md pour le brief
// complet et les captures de référence.
export const WALTER_COLORS = {
  /** Accent Walter Learning : porte l'action, toujours avec un texte noir dessus. */
  strawYellow: '#FCC14A',
  strawYellowDark: '#F3B437',
  /** Accent Walter Santé. */
  azureBlue: '#3A90F2',
  azureBlueDark: '#2A7FDE',
  /** Blanc cassé chaud : fond de section et de page, puces discrètes. */
  offWhite: '#F4F1EC',
  /** Neutre chaud plus soutenu, pour les grands aplats. */
  mastic: '#CCC1A8',
  /** Bordure douce dérivée des neutres chauds (les sites n'utilisent pas de gris froid). */
  border: '#E7E1D8',
  black: '#000000',
  /** Noir de texte côté Santé, légèrement adouci. */
  santeBlack: '#0A0A0A',
  coral: '#F0512A',
  denimBlue: '#455AC4',
  linkBlue: '#0070E0',
  green: '#008049',
  orange: '#FF8400',
} as const

export const RADII = {
  /** Boutons et puces : pilule pleine (`9999px` sur les sites). */
  pill: 999,
  card: 16,
  media: 12,
} as const

export type BrandDomainType = 'learning' | 'sante' | 'edumiam'

const ACCENTS: Record<BrandDomainType, { main: string; dark: string; contrastText: string }> = {
  learning: { main: WALTER_COLORS.strawYellow, dark: WALTER_COLORS.strawYellowDark, contrastText: WALTER_COLORS.black },
  sante: { main: WALTER_COLORS.azureBlue, dark: WALTER_COLORS.azureBlueDark, contrastText: '#FFFFFF' },
  // Bleu Edumiam (`EdumiamTheme`), seule marque hors Walter à habiller la marketplace.
  edumiam: { main: '#2e5581', dark: '#1f3d5e', contrastText: '#FFFFFF' },
}

const BASE_THEMES: Record<BrandDomainType, typeof WLtheme> = {
  learning: WLtheme,
  sante: WStheme,
  edumiam: EdumiamTheme,
}

/**
 * Étend le thème de la marque avec la direction artistique des sites vitrines : neutres chauds,
 * boutons en pilule sans capitales ni ombre, un unique accent par marque.
 *
 * Volontairement dérivé plutôt qu'appliqué à `WLtheme` / `WStheme` : ces thèmes habillent la
 * vingtaine de pages du tunnel d'inscription, hors périmètre de cette itération. À n'utiliser que
 * dans un `ThemeProvider` imbriqué.
 */
export const buildMarketplaceTheme = (domain: BrandDomainType) => {
  const accent = ACCENTS[domain]
  const ink = domain === 'sante' ? WALTER_COLORS.santeBlack : WALTER_COLORS.black
  // Le titrage n'existe qu'en graisse 400 : demander plus déclencherait un faux gras de synthèse.
  const display = { fontWeight: 400, letterSpacing: 'normal', lineHeight: 1.08 }

  return responsiveFontSizes(
    createTheme(BASE_THEMES[domain], {
      palette: {
        primary: {
          main: accent.main,
          dark: accent.dark,
          light: alpha(accent.main, 0.12),
          contrastText: accent.contrastText,
        },
        background: { default: WALTER_COLORS.offWhite, paper: '#FFFFFF' },
        text: { primary: ink, secondary: alpha(ink, 0.6), disabled: alpha(ink, 0.38) },
        divider: WALTER_COLORS.border,
        success: { main: WALTER_COLORS.green },
        warning: { main: WALTER_COLORS.orange },
      },
      shape: { borderRadius: 8 },
      typography: {
        h3: display,
        h4: display,
        h5: display,
        h6: display,
        button: { textTransform: 'none', fontWeight: 500, fontSize: 15 },
      },
      components: {
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: RADII.pill,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              paddingLeft: 24,
              paddingRight: 24,
              '&:hover': { boxShadow: 'none' },
            },
            containedPrimary: {
              backgroundColor: accent.main,
              color: accent.contrastText,
              borderRadius: RADII.pill,
              '&:hover': { backgroundColor: accent.dark, color: accent.contrastText },
            },
            outlinedPrimary: {
              backgroundColor: 'transparent',
              borderColor: ink,
              color: ink,
              borderRadius: RADII.pill,
              '&:hover': { backgroundColor: alpha(ink, 0.06), borderColor: ink, color: ink },
            },
            textPrimary: {
              color: ink,
              '&:hover': { backgroundColor: alpha(ink, 0.06) },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: RADII.card,
              borderColor: WALTER_COLORS.border,
              boxShadow: 'none',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: { fontWeight: 500 },
            filledDefault: { backgroundColor: WALTER_COLORS.offWhite, color: ink },
            outlined: { borderColor: WALTER_COLORS.border, color: ink },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: { borderRadius: RADII.pill },
          },
        },
        MuiAccordion: {
          styleOverrides: {
            root: {
              borderColor: WALTER_COLORS.border,
              '&:first-of-type': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
              '&:last-of-type': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 },
            },
          },
        },
      },
    }),
  )
}
